/**
 * Plaid Foundations Icon
 *
 * Shamelessly adapted from LWC:
 * @see [redacted]##################################################################################
 */

import { Suspense, useEffect, useId, useState, type FC } from 'react';
import { ConditionalWrapper } from 'src/components-v2/Helpers';
import {
  LazyIconModules,
  type IconName,
} from 'src/lib/foundation-icon-imports';
import styled from 'threads5/styles/styled';
import type { IIconProps } from './Icon';

export type FoundationIconSize = IIconProps['size'];

export type FoundationIconName = IconName;

// Based off of Threads5:
// @see [redacted]###########################################################################################################
const getIconWrapperPadding = (iconSize: FoundationIconSize): number => {
  switch (iconSize) {
    case 14:
    case 18:
      return 1;
    case 16:
    case 20:
      return 2;
    default:
      return 0;
  }
};

/**
 * Hook to apply unique clip path IDs to SVG icons to prevent conflicts
 * in the DOM and flaky rendering behavior. This would ideally
 * be handled by the icon package in the future.
 *
 * Expected SVG shape:
 * ```
 * <g clip-path='url(#foo)' />
 * <defs>
 *  <clipPath id='foo'>
 *    ...
 *  </clipPath>
 *  ...
 * </defs>
 * ```
 *
 * Borrowed from Threads5:
 * @see [redacted]#######################################################################################################
 */
export const useUniqueSVGClipPathId = (
  pathContainingElement: Element | null,
) => {
  const clipPathId = useId();

  useEffect(() => {
    if (!pathContainingElement) {
      return;
    }

    const groupNode = pathContainingElement.querySelector('g');
    groupNode?.setAttribute('clip-path', `url(#${clipPathId})`);

    const clipPath = pathContainingElement.querySelector('clipPath');
    clipPath?.setAttribute('id', clipPathId);
  }, [clipPathId, pathContainingElement]);
};

const StyledWrapper = styled('i', {
  shouldForwardProp: (prop: string) => {
    return prop !== 'size';
  },
})<{
  size: FoundationIconSize;
}>(({ size }) => {
  const padding = getIconWrapperPadding(size);

  return {
    padding: `${padding}px`,
    display: 'inline-flex',
  };
});

const StyledPlaceholderIcon = styled('span', {
  shouldForwardProp: (prop: string) => {
    return prop !== 'size';
  },
})<{ size: FoundationIconSize }>(({ size }) => {
  return {
    width: `${size}px`,
    height: `${size}px`,
    display: 'inline-block',
  };
});

/** Memoized styled components for each icon */
const StyledFoundationIcons = new Map<
  string,
  ReturnType<ReturnType<typeof styled>>
>();

const FoundationIcon: FC<
  Omit<IIconProps, 'icon'> & { icon: FoundationIconName; omitWrapper?: boolean }
> = ({ icon, size = 16, omitWrapper = false, ...rest }) => {
  const moduleName = `${icon}${size}`;
  const [svgElement, setSVGElement] = useState<SVGElement | null>(null);

  useUniqueSVGClipPathId(svgElement);

  let StyledLazyIconComponent = StyledFoundationIcons.get(moduleName);

  if (!StyledLazyIconComponent) {
    const LazyIconComponent = LazyIconModules[moduleName];
    if (!LazyIconComponent) {
      // eslint-disable-next-line no-console
      console.error(`Icon ${moduleName} not found`);
      return null;
    }

    StyledLazyIconComponent = styled(LazyIconComponent)({});
    StyledFoundationIcons.set(moduleName, StyledLazyIconComponent);
  }

  return (
    <ConditionalWrapper
      condition={!omitWrapper}
      wrapper={(children) => {
        return <StyledWrapper size={size}>{children}</StyledWrapper>;
      }}
    >
      {/* `data-icon-loading`: Happo waits for these to clear before capture (.storybook/lib/happo.ts). */}
      <Suspense
        fallback={<StyledPlaceholderIcon size={size} data-icon-loading />}
      >
        <StyledLazyIconComponent ref={setSVGElement} {...rest} />
      </Suspense>
    </ConditionalWrapper>
  );
};

export default FoundationIcon;
